/**
 * RBAC 页面/API 清单（读取 rbacPages.json）
 *
 * 结构说明（v2）：
 * - pages[].module = 第一级 Seeder 父节点（如「活动管理」）
 * - pages[].name = 具体页面/子页（如「会议日程」）
 * - pages[].apis[].name = 第二级权限（middleware / 前端 guard）
 */
import rbacPagesDefinition from './rbacPages.json'

export { rbacPagesDefinition }

/** @typedef {{ name: string, httpMethod?: string, apiPath?: string, ui?: string[], note?: string, status?: string }} RbacApiDef */
/** @typedef {{ module: string, name: string, routePath?: string, routePathEdit?: string, routeNames?: string[], menuKey?: string, navKey?: string, apis?: RbacApiDef[], status?: string, superAdminOnly?: boolean }} RbacPageDef */

/** @type {RbacPageDef[]} */
export const RBAC_PAGES = rbacPagesDefinition.pages || []

/**
 * 所有第一级模块名（Seeder 父节点，去重）
 * @returns {string[]}
 */
export function listRbacModules () {
  return [...new Set(RBAC_PAGES.map((p) => p.module).filter(Boolean))]
}

/**
 * 按 module 分组页面
 * @returns {Record<string, RbacPageDef[]>}
 */
export function groupPagesByModule () {
  /** @type {Record<string, RbacPageDef[]>} */
  const map = {}
  for (const page of RBAC_PAGES) {
    const mod = page.module || '未分类'
    if (!map[mod]) map[mod] = []
    map[mod].push(page)
  }
  return map
}

/**
 * 扁平化所有第二级 API 权限名（去重，不含 proposed 空名）
 * @returns {string[]}
 */
export function listAllRbacApiNames () {
  const names = new Set()
  for (const page of RBAC_PAGES) {
    for (const api of page.apis || []) {
      if (api.name && api.status !== 'proposed') names.add(String(api.name).trim())
    }
  }
  return [...names]
}

/** 按 module + 页面名查找 */
export function findRbacPage (module, pageName) {
  return RBAC_PAGES.find((p) => p.module === module && p.name === pageName) || null
}

/** 按 module 查找该模块下全部子页 */
export function findRbacPagesByModule (module) {
  return RBAC_PAGES.filter((p) => p.module === module)
}

/**
 * 按 B 端路由 path 匹配页面（优先最长 routePath / routePathEdit 前缀）
 * @param {string} path
 */
export function findRbacPageByRoute (path) {
  const p = String(path || '')
  const candidates = []

  for (const page of RBAC_PAGES) {
    for (const base of [page.routePath, page.routePathEdit]) {
      if (!base) continue
      const pattern = base.replace(/\{[^}]+\}/g, '[^/]+')
      const re = new RegExp(`^${pattern.replace(/\//g, '\\/')}(\\/|$)`)
      if (re.test(p) || p === base.replace(/\{[^}]+\}/g, '')) {
        candidates.push({ page, len: base.length })
      }
    }
    if (page.routeNames?.length) {
      /* routeNames 需配合 router，此处仅作 path 兜底 */
    }
  }

  candidates.sort((a, b) => b.len - a.len)
  return candidates[0]?.page || null
}

/**
 * 所有 JSON 中定义的「分组名」（module + page.name），guard 应排除 merge 进来的父级名
 */
export function listRbacGroupNames () {
  const names = new Set()
  for (const page of RBAC_PAGES) {
    if (page.module) names.add(String(page.module).trim())
    if (page.name) names.add(String(page.name).trim())
  }
  return [...names]
}

/**
 * 从 rbacPages v2 平铺 pages 构建嵌套树（联调对照 GET /permissions/tree）
 * module(group) → page.name(group) → apis[](api 叶子)
 * @param {RbacPageDef[]} [pages]
 */
export function buildPermissionTreeFromPages (pages = RBAC_PAGES) {
  const modules = new Map()
  let tempId = -1
  const nextTempId = () => tempId -= 1

  for (const page of pages) {
    const mod = page.module || '未分类'
    const apiChildren = (page.apis || [])
      .filter((a) => a.name && a.status !== 'proposed')
      .map((a) => ({
        id: nextTempId(),
        name: a.name,
        type: 'api',
        httpMethod: a.httpMethod,
        apiPath: a.apiPath,
        description: `${a.httpMethod || ''} ${a.apiPath || ''}`.trim()
      }))

    if (!apiChildren.length && (page.status === 'placeholder' || page.status === 'internal')) {
      continue
    }

    if (!modules.has(mod)) {
      modules.set(mod, {
        id: nextTempId(),
        name: mod,
        type: 'group',
        children: []
      })
    }

    modules.get(mod).children.push({
      id: nextTempId(),
      name: page.name,
      type: 'group',
      routePath: page.routePath,
      routePathEdit: page.routePathEdit,
      navKey: page.navKey,
      children: apiChildren
    })
  }

  return [...modules.values()]
}

/**
 * 按路由 path 解析页面默认入口叶子权限（ui 含 route 的 api，否则取首个 api）
 * @param {string} path
 * @returns {string|null}
 */
export function resolveRouteEntryPermission (path) {
  const page = findRbacPageByRoute(path)
  if (!page?.apis?.length) return null
  const apis = page.apis.filter((a) => a.name && a.status !== 'proposed')
  if (!apis.length) return null
  const routeApi = apis.find((a) =>
    (a.ui || []).some((u) => u === 'route' || String(u).startsWith('route:'))
  )
  return routeApi?.name || apis[0].name
}

/** module 英文名前缀 */
const MODULE_KEY_MAP = {
  相册管理: 'ALBUM',
  活动管理: 'ACTIVITY',
  报名管理: 'REGISTRATION',
  酒店管理: 'HOTEL',
  账号管理: 'ACCOUNT',
  角色管理: 'ROLE',
  权限管理: 'PERMISSION',
  公司管理: 'COMPANY',
  ACL管理: 'RESOURCE_GRANT',
  微站模板: 'MICROSITE_TEMPLATE',
  商协会管理: 'ASSOCIATION',
  会员套餐: 'PLAN',
  充值套餐: 'RECHARGE'
}

/** apis[].name → 常量后缀 */
const API_SUFFIX = {
  查看相册列表: 'LIST',
  创建相册: 'CREATE',
  编辑相册: 'EDIT',
  删除相册: 'DELETE',
  查看相册详情: 'VIEW',
  编辑相册展示配置: 'DISPLAY_SETTINGS',
  管理相册成员: 'MEMBERS_LIST',
  分配相册成员角色: 'MEMBERS_ASSIGN',
  查看相册照片: 'PHOTOS_LIST',
  上传相册照片: 'PHOTOS_UPLOAD',
  编辑相册照片: 'PHOTOS_EDIT',
  删除相册照片: 'PHOTOS_DELETE',
  管理照片分组: 'PHOTO_GROUPS_LIST',
  创建照片分组: 'PHOTO_GROUPS_CREATE',
  编辑照片分组: 'PHOTO_GROUPS_EDIT',
  删除照片分组: 'PHOTO_GROUPS_DELETE',
  审核照片: 'AUDIT_PHOTO',
  'AI修图体验': 'AI_RETOUCH',
  查看活动列表: 'LIST',
  创建活动: 'CREATE',
  编辑活动: 'EDIT',
  删除活动: 'DELETE',
  查看活动详情: 'VIEW',
  查看会议日程列表: 'SCHEDULE_LIST',
  创建会议日程: 'SCHEDULE_CREATE',
  编辑会议日程: 'SCHEDULE_EDIT',
  删除会议日程: 'SCHEDULE_DELETE',
  查看座位分配列表: 'SEAT_LIST',
  创建座位分配: 'SEAT_CREATE',
  编辑座位分配: 'SEAT_EDIT',
  删除座位分配: 'SEAT_DELETE',
  导入座位分配: 'SEAT_IMPORT',
  导出席位分配: 'SEAT_EXPORT',
  查看座位查询页: 'SEAT_QUERY_VIEW',
  保存座位查询页: 'SEAT_QUERY_SAVE',
  查看签到配置: 'CHECKIN_VIEW',
  创建签到: 'CHECKIN_CREATE',
  保存签到配置: 'CHECKIN_SAVE',
  删除签到: 'CHECKIN_DELETE',
  查看签到名单: 'CHECKIN_USERS',
  导入签到名单: 'CHECKIN_IMPORT',
  导出签到名单: 'CHECKIN_EXPORT',
  新增签到人员: 'CHECKIN_USER_CREATE',
  编辑签到人员: 'CHECKIN_USER_EDIT',
  查看活动任务人员: 'TASK_PEOPLE_LIST',
  新增活动任务人员: 'TASK_PEOPLE_CREATE',
  导入活动任务人员: 'TASK_PEOPLE_IMPORT',
  查看活动任务人员详情: 'TASK_PEOPLE_VIEW',
  编辑活动任务人员: 'TASK_PEOPLE_EDIT',
  删除活动任务人员: 'TASK_PEOPLE_DELETE',
  查看微站页面列表: 'MICROSITE_PAGES_LIST',
  创建微站页面: 'MICROSITE_PAGES_CREATE',
  删除微站页面: 'MICROSITE_PAGES_DELETE',
  更新微站页面元信息: 'MICROSITE_PAGES_PATCH',
  查看微站页面详情: 'MICROSITE_PAGE_VIEW',
  保存微站页面: 'MICROSITE_PAGE_SAVE',
  活动OSS上传凭证: 'OSS_POST_POLICY',
  配置活动对接邀请邮件: 'MAIL_TEMPLATE',
  配置活动分享模板: 'SHARE_TEMPLATE',
  查看报名表单列表: 'LIST',
  创建报名表单: 'CREATE',
  编辑报名表单: 'EDIT',
  删除报名表单: 'DELETE',
  复制报名表单: 'COPY',
  查看报名表单详情: 'VIEW',
  关联表单与会议: 'LINK_MEETING',
  查看报名提交列表: 'SUBMISSION_LIST',
  编辑报名提交: 'SUBMISSION_EDIT',
  删除报名提交: 'SUBMISSION_DELETE',
  审核报名提交: 'SUBMISSION_REVIEW',
  创建报名订单: 'ORDER_CREATE',
  查看报名订单列表: 'ORDER_LIST',
  查看报名订单详情: 'ORDER_VIEW',
  取消报名订单: 'ORDER_CANCEL',
  发起报名订单支付: 'ORDER_PAY',
  查询报名订单支付状态: 'ORDER_PAY_STATUS',
  报名订单退款: 'ORDER_REFUND',
  管理对接人名单: 'PROXY_BATCH_LIST',
  创建对接人名单: 'PROXY_BATCH_CREATE',
  编辑对接人名单: 'PROXY_BATCH_EDIT',
  删除对接人名单: 'PROXY_BATCH_DELETE',
  发布对接人名单: 'PROXY_BATCH_PUBLISH',
  重发对接人邮件: 'PROXY_BATCH_RESEND_MAIL',
  查看对接人代填记录: 'PROXY_SUBMISSION_LIST',
  查看对接人会议列表: 'PROXY_MEETING_LIST',
  对接人录入: 'PROXY_ENTRY',
  编辑对接人代填记录: 'PROXY_SUBMISSION_EDIT',
  查看酒店列表: 'LIST',
  创建酒店: 'CREATE',
  查看酒店详情: 'VIEW',
  编辑酒店: 'EDIT',
  删除酒店: 'DELETE',
  查看酒店房型列表: 'ROOM_LIST',
  创建酒店房型: 'ROOM_CREATE',
  编辑酒店房型: 'ROOM_EDIT',
  删除酒店房型: 'ROOM_DELETE',
  酒店OSS上传凭证: 'OSS_POST_POLICY',
  酒店OSS对象删除: 'OSS_DELETE',
  查看账号列表: 'LIST',
  创建账号: 'CREATE',
  编辑账号: 'EDIT',
  分配角色: 'ASSIGN_ROLE',
  关联到公司: 'ASSOCIATE_COMPANY',
  解除关联: 'DISSOCIATE_COMPANY',
  搜索账号: 'SEARCH',
  查看角色列表: 'LIST',
  创建角色: 'CREATE',
  编辑角色: 'EDIT',
  删除角色: 'DELETE',
  分配权限: 'ASSIGN_PERMISSION',
  查看权限列表: 'LIST',
  创建权限: 'CREATE',
  编辑权限: 'EDIT',
  删除权限: 'DELETE',
  查看公司列表: 'LIST',
  创建公司: 'CREATE',
  编辑公司: 'EDIT',
  删除公司: 'DELETE',
  管理公司成员: 'MEMBERS_LIST',
  查看会员概况: 'MEMBER_STATS',
  查看资源授权列表: 'LIST',
  创建资源授权: 'CREATE',
  查看资源授权详情: 'VIEW',
  编辑资源授权: 'EDIT',
  撤销资源授权: 'REVOKE',
  查看微站模板列表: 'TEMPLATE_LIST',
  创建微站模板: 'TEMPLATE_CREATE',
  查看当前套餐: 'CURRENT',
  查看套餐列表: 'CATALOG',
  购买套餐: 'PURCHASE',
  发起套餐支付: 'PAY',
  查询套餐支付状态: 'PAY_STATUS',
  取消套餐订单: 'CANCEL',
  查看套餐购买记录: 'ORDER_LIST',
  查看套餐档位: 'ADMIN_LIST',
  创建套餐档位: 'ADMIN_CREATE',
  编辑套餐档位: 'ADMIN_EDIT',
  删除套餐档位: 'ADMIN_DELETE',
  给公司开通套餐: 'GRANT',
  查看套餐开通记录: 'ADMIN_ORDER_LIST',
  查看全平台存储: 'CLOUD_SPACE_ADMIN',
  查看企业存储明细: 'CLOUD_SPACE_DETAIL',
  查看充值套餐: 'CATALOG',
  查看我的充值套餐: 'CREDITS',
  查看充值记录: 'ORDER_LIST',
  购买充值套餐: 'PURCHASE',
  发起充值支付: 'PAY',
  查询充值支付状态: 'PAY_STATUS',
  取消充值订单: 'CANCEL',
  查看平台充值记录: 'ADMIN_ORDER_LIST',
  查看充值数据概览: 'ADMIN_OVERVIEW',
  查看充值资源类型: 'ADMIN_TYPE_LIST',
  创建充值资源类型: 'ADMIN_TYPE_CREATE',
  编辑充值资源类型: 'ADMIN_TYPE_EDIT',
  删除充值资源类型: 'ADMIN_TYPE_DELETE',
  配置充值套餐档位: 'ADMIN_TYPE_PACKAGES',
  充值资源封面上传凭证: 'ADMIN_COVER_POLICY',
  确认充值资源封面: 'ADMIN_COVER_CONFIRM',
  查看协会管理: 'MANAGE_VIEW',
  编辑小程序首页: 'HOME_EDIT',
  发布小程序首页: 'HOME_PUBLISH',
  管理会费设置: 'FEE_SETTINGS',
  管理小程序素材: 'ASSETS_MANAGE',
  管理协会活动: 'ACTIVITIES',
  管理协会供需: 'DEMANDS',
  管理协会文章: 'ARTICLES',
  管理小程序配置: 'MINI_PROGRAM_CONFIG'
}

/**
 * 从 JSON 生成 RBAC 常量：{ ACTIVITY_SCHEDULE_LIST: '查看会议日程列表', ... }
 * 同一 module 下若 suffix 冲突会加页面名缩写（少见）
 */
export function buildRbacConstantsFromPages () {
  /** @type {Record<string, string>} */
  const out = {}
  for (const page of RBAC_PAGES) {
    const prefix = MODULE_KEY_MAP[page.module]
    if (!prefix) continue
    for (const api of page.apis || []) {
      if (!api.name || api.status === 'proposed') continue
      const suffix = API_SUFFIX[api.name]
      if (!suffix) continue
      let key = `${prefix}_${suffix}`
      if (out[key] && out[key] !== api.name) {
        key = `${prefix}_${suffix}_ALT`
      }
      out[key] = api.name
    }
  }
  return out
}

/** @type {Record<string, RbacApiDef[]>} 按 module 聚合去重后的 API 列表（供 Seeder 生成参考） */
export function buildSeederTreeFromPages () {
  const map = groupPagesByModule()
  return Object.fromEntries(
    Object.entries(map).map(([mod, pages]) => {
      const apiMap = new Map()
      for (const page of pages) {
        for (const api of page.apis || []) {
          if (api.name) apiMap.set(api.name, api)
        }
      }
      return [mod, [...apiMap.values()]]
    })
  )
}

export const RBAC_MIGRATION_NOTES = rbacPagesDefinition._meta?.migration || {}
